import { AUTO_DETECT } from "./languages";

// Chrome's translator uses base BCP-47 tags, so the few Google codes that differ are mapped over.
const BROWSER_CODES = {
    "zh-cn": "zh",
    "zh-tw": "zh-Hant",
    "iw": "he",
    "jw": "jv",
    "fil": "tl",
};

const toBrowserCode = (code) => BROWSER_CODES[String(code || "").toLowerCase()] || code;

// Chrome 138+ and current Edge expose Translator globally; older builds put it on window.translation.
const getTranslatorApi = () => {
    if (typeof window === "undefined") return null;
    if (window.Translator?.create) return window.Translator;
    if (window.translation?.createTranslator) return window.translation;
    return null;
};

const getDetectorApi = () => {
    if (typeof window === "undefined") return null;
    if (window.LanguageDetector?.create) return window.LanguageDetector;
    return null;
};

// The legacy shape reports readiness through canTranslate instead of availability.
const readAvailability = async (api, pair) => {
    if (api.availability) return await api.availability(pair);
    if (api.canTranslate) return await api.canTranslate(pair);
    return "unavailable";
};

const isUsable = (availability) => availability && availability !== "unavailable" && availability !== "no";

// A model that still has to download reports its progress so the UI can say why it is waiting.
const buildMonitor = (onStatus) => (monitor) => {
    monitor.addEventListener("downloadprogress", (event) => {
        const total = event.total || 1;
        const percent = Math.round((event.loaded / total) * 100);
        onStatus?.(`Downloading the language pack... ${percent}%`);
    });
};

const detectLanguage = async (text, onStatus) => {
    const DetectorApi = getDetectorApi();
    if (!DetectorApi) return null;

    const availability = await DetectorApi.availability?.();
    if (availability && !isUsable(availability)) return null;

    const detector = await DetectorApi.create({ monitor: buildMonitor(onStatus) });
    try {
        const [best] = (await detector.detect(text)) || [];
        return best?.detectedLanguage || null;
    } finally {
        detector.destroy?.();
    }
};

// The browser's own on-device translator: no key, no network round trip, no quota.
export const browserTranslator = {
    id: "browser",
    label: "Chrome / Edge built-in translator",

    async isAvailable({ sourceLanguage, targetLanguage }) {
        const api = getTranslatorApi();
        if (!api) return false;
        // Auto detection needs the companion detector API, so without it Google takes over.
        if (sourceLanguage === AUTO_DETECT && !getDetectorApi()) return false;
        if (sourceLanguage !== AUTO_DETECT) {
            const availability = await readAvailability(api, {
                sourceLanguage: toBrowserCode(sourceLanguage),
                targetLanguage: toBrowserCode(targetLanguage),
            });
            return isUsable(availability);
        }
        return true;
    },

    async translate({ text, sourceLanguage, targetLanguage, onStatus }) {
        const api = getTranslatorApi();
        if (!api) throw new Error("This browser has no built-in translator");

        let resolvedSource = sourceLanguage;
        if (resolvedSource === AUTO_DETECT) {
            onStatus?.("Detecting the language...");
            resolvedSource = await detectLanguage(text, onStatus);
            if (!resolvedSource) throw new Error("The browser could not detect the language");
        }

        const pair = {
            sourceLanguage: toBrowserCode(resolvedSource),
            targetLanguage: toBrowserCode(targetLanguage),
        };

        if (pair.sourceLanguage === pair.targetLanguage) {
            return { text, detectedLanguage: resolvedSource };
        }

        const availability = await readAvailability(api, pair);
        if (!isUsable(availability)) throw new Error("This language pair is not installed in the browser");

        onStatus?.("Translating on your device...");
        const translator = api.create
            ? await api.create({ ...pair, monitor: buildMonitor(onStatus) })
            : await api.createTranslator(pair);

        try {
            const translated = await translator.translate(text);
            return { text: translated, detectedLanguage: resolvedSource };
        } finally {
            translator.destroy?.();
        }
    },
};
